import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from 'src/environments/environment';

import {
  ManageAssetAssignRequestModel,
  ManageAssetAssignmentResultsModel,
  ManageAssetAssignmentSummaryModel,
  ManageAssetAssignmentsRequestModel,
  ManageAssetBarcodeLookupRequestModel,
  ManageAssetBarcodeLookupResultModel,
  ManageAssetCampusModel,
  ManageAssetEmployeeModel,
  ManageAssetGroupMembersRequestModel,
  ManageAssetLocationModel,
  ManageAssetReturnBatchRequestModel,
  ManageAssetReturnRequestModel,
  ManageAssetSearchRequestModel,
  ManageAssetSearchResultModel,
  ManageAssetTypeModel,
} from 'src/app/models/manageAssetModels';

@Injectable({ providedIn: 'root' })
export class ManageAssetService {
  private readonly manageAssetApiPath = `${environment.apiServer.webApiURL}/ManageAsset`;

  constructor(private http: HttpClient) {}

  getAssetTypes(): Observable<ManageAssetTypeModel[]> {
    return this.http.get<ManageAssetTypeModel[]>(`${this.manageAssetApiPath}/AssetTypes`);
  }

  getAssignableEmployees(includeSeparated = false): Observable<ManageAssetEmployeeModel[]> {
    return this.http.get<ManageAssetEmployeeModel[]>(
      `${this.manageAssetApiPath}/AssignableEmployees`,
      { params: { includeSeparated: includeSeparated ? 'true' : 'false' } });
  }

  getAssignableCampuses(): Observable<ManageAssetCampusModel[]> {
    return this.http.get<ManageAssetCampusModel[]>(`${this.manageAssetApiPath}/AssignableCampuses`);
  }

  getAssetLocations(): Observable<ManageAssetLocationModel[]> {
    return this.http.get<ManageAssetLocationModel[]>(`${this.manageAssetApiPath}/AssetLocations`);
  }

  getAssetGroups(): Observable<string[]> {
    return this.http.get<string[]>(`${this.manageAssetApiPath}/AssetGroups`);
  }

  searchAvailableAssets(request: ManageAssetSearchRequestModel): Observable<ManageAssetSearchResultModel[]> {
    return this.http.post<ManageAssetSearchResultModel[]>(`${this.manageAssetApiPath}/SearchAvailableAssets`, request);
  }

  getGroupMembersForSelection(request: ManageAssetGroupMembersRequestModel): Observable<ManageAssetSearchResultModel[]> {
    return this.http.post<ManageAssetSearchResultModel[]>(`${this.manageAssetApiPath}/GroupMembersForSelection`, request);
  }

  getAssignedAssets(request: ManageAssetAssignmentsRequestModel): Observable<ManageAssetAssignmentResultsModel> {
    return this.http.post<ManageAssetAssignmentResultsModel>(`${this.manageAssetApiPath}/AssignedAssets`, request);
  }

  assignAssets(request: ManageAssetAssignRequestModel[]): Observable<ManageAssetAssignmentSummaryModel[]> {
    return this.http.post<ManageAssetAssignmentSummaryModel[]>(`${this.manageAssetApiPath}/AssignAssets`, request);
  }

  lookupBarcode(request: ManageAssetBarcodeLookupRequestModel): Observable<ManageAssetBarcodeLookupResultModel> {
    return this.http.post<ManageAssetBarcodeLookupResultModel>(`${this.manageAssetApiPath}/LookupBarcode`, request);
  }

  returnAsset(request: ManageAssetReturnRequestModel): Observable<ManageAssetAssignmentSummaryModel> {
    return this.http.post<ManageAssetAssignmentSummaryModel>(`${this.manageAssetApiPath}/ReturnAsset`, request);
  }

  returnAssets(request: ManageAssetReturnBatchRequestModel): Observable<ManageAssetAssignmentSummaryModel[]> {
    return this.http.post<ManageAssetAssignmentSummaryModel[]>(`${this.manageAssetApiPath}/ReturnAssets`, request.items);
  }

  reassignAssets(request: ManageAssetReturnBatchRequestModel): Observable<ManageAssetAssignmentSummaryModel[]> {
    return this.http.post<ManageAssetAssignmentSummaryModel[]>(`${this.manageAssetApiPath}/ReassignAssets`, request.items);
  }

  getAssignmentDocument(assetAssignedID: number, returned: boolean): Observable<ArrayBuffer> {
    const route = returned ? 'ReturnDocument' : 'AssignmentDocument';
    return this.http.get<ArrayBuffer>(`${this.manageAssetApiPath}/${route}/${assetAssignedID}`, {
      responseType: 'arraybuffer' as 'json'
    });
  }

  signAssignmentDocument(assetAssignedID: number, signedFields: string[]): Observable<ArrayBuffer> {
    return this.http.post<ArrayBuffer>(`${this.manageAssetApiPath}/SignAssignmentDocument/${assetAssignedID}`,
      signedFields,
      { responseType: 'arraybuffer' as 'json' });
  }
}
